import React, { useEffect, useState, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { TrendingUp, Plus, Trash2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import {
  getWinePrices, addWinePrice, deleteWinePrice, getMyWinePurchaseHistory,
} from '../lib/api';

const fmtMoney = (v, currency = 'USD') =>
  v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(v);

const TooltipContent = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-stone-950 border border-[#d4af37]/40 px-4 py-2 text-xs">
      {payload.map((p) => (
        <div key={p.dataKey} className="text-stone-200">
          <span className="text-stone-500 uppercase tracking-widest text-[10px] mr-2">
            {p.dataKey === 'market' ? 'Market' : 'My Purchase'}:
          </span>
          {fmtMoney(p.value)}
        </div>
      ))}
      <div className="text-stone-500 text-[10px] mt-1">{payload[0]?.payload?.date}</div>
    </div>
  );
};

export const WinePriceHistory = ({ wineId, isAdmin, isAuthenticated }) => {
  const [marketPrices, setMarketPrices] = useState([]);
  const [myPurchases, setMyPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ price: '', currency: 'USD', source: '', recorded_at: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!wineId) return;
    setLoading(true);
    try {
      const [m, p] = await Promise.all([
        getWinePrices(wineId).catch(() => []),
        isAuthenticated ? getMyWinePurchaseHistory(wineId).catch(() => []) : Promise.resolve([]),
      ]);
      setMarketPrices(Array.isArray(m) ? m : []);
      setMyPurchases(Array.isArray(p) ? p : []);
    } finally {
      setLoading(false);
    }
  }, [wineId, isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.price || parseFloat(form.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    setSaving(true);
    try {
      await addWinePrice(wineId, {
        price: parseFloat(form.price),
        currency: form.currency || 'USD',
        source: form.source || '',
        recorded_at: form.recorded_at ? new Date(form.recorded_at).toISOString() : undefined,
      });
      toast.success('Price point added');
      setForm({ price: '', currency: 'USD', source: '', recorded_at: '' });
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add price');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (priceId) => {
    if (!window.confirm('Delete this price point?')) return;
    try {
      await deleteWinePrice(wineId, priceId);
      toast.success('Price point deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // Merge market + personal series by date for the chart
  const chartData = (() => {
    const byDate = new Map();
    marketPrices.forEach((p) => {
      const d = (p.recorded_at || '').slice(0, 10);
      const cur = byDate.get(d) || { date: d };
      cur.market = Number(p.price);
      byDate.set(d, cur);
    });
    myPurchases.forEach((p) => {
      const d = (p.purchase_date || '').slice(0, 10) || (p.created_at || '').slice(0, 10);
      const cur = byDate.get(d) || { date: d };
      cur.purchase = Number(p.purchase_price);
      byDate.set(d, cur);
    });
    return Array.from(byDate.values())
      .filter((r) => r.date)
      .sort((a, b) => a.date.localeCompare(b.date));
  })();

  const latestMarket = marketPrices.length ? marketPrices[marketPrices.length - 1].price : null;
  const myAvg = myPurchases.length
    ? myPurchases.reduce((s, p) => s + Number(p.purchase_price || 0) * (p.quantity || 1), 0) /
      myPurchases.reduce((s, p) => s + (p.quantity || 1), 0)
    : null;

  return (
    <div className="border border-white/5 bg-stone-900/30 p-6 md:p-8" data-testid="wine-price-history">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-1 flex items-center gap-2">
            <TrendingUp className="w-3 h-3" /> Price History
          </p>
          <h3 className="text-2xl font-serif text-stone-50">Market &amp; Personal Tracking</h3>
        </div>
        {isAdmin && (
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs px-5 py-5"
                data-testid="add-price-btn"
              >
                <Plus className="w-3 h-3 mr-2" /> Add Market Price
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-stone-950 border border-white/10 text-stone-200 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-stone-50 font-serif">Add a market price point</DialogTitle>
                <DialogDescription className="text-stone-500 text-xs">
                  Record a new market price observation for this wine. Date, currency and source are optional.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Price</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="bg-stone-900 border-stone-700 text-stone-100 rounded-none"
                    data-testid="price-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Currency</Label>
                    <Input
                      value={form.currency}
                      onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                      className="bg-stone-900 border-stone-700 text-stone-100 rounded-none"
                      data-testid="currency-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Date</Label>
                    <Input
                      type="date"
                      value={form.recorded_at}
                      onChange={(e) => setForm({ ...form, recorded_at: e.target.value })}
                      className="bg-stone-900 border-stone-700 text-stone-100 rounded-none"
                      data-testid="date-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Source (e.g. Wine.com)</Label>
                  <Input
                    value={form.source}
                    onChange={(e) => setForm({ ...form, source: e.target.value })}
                    className="bg-stone-900 border-stone-700 text-stone-100 rounded-none"
                    data-testid="source-input"
                  />
                </div>
                <Button
                  onClick={handleAdd}
                  disabled={saving}
                  className="w-full bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs py-5"
                  data-testid="save-price-btn"
                >
                  {saving ? 'Saving…' : 'Save Price Point'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="border border-white/5 bg-stone-950/40 p-4">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Latest Market</p>
          <p className="text-xl font-serif text-stone-50">{fmtMoney(latestMarket)}</p>
        </div>
        <div className="border border-white/5 bg-stone-950/40 p-4">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">My Avg Paid</p>
          <p className="text-xl font-serif text-stone-50">{fmtMoney(myAvg)}</p>
        </div>
        <div className="border border-white/5 bg-stone-950/40 p-4">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Data Points</p>
          <p className="text-xl font-serif text-stone-50">{marketPrices.length} <span className="text-stone-500 text-sm">market</span> · {myPurchases.length} <span className="text-stone-500 text-sm">mine</span></p>
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <p className="text-sm text-stone-500 py-12 text-center">Loading price history…</p>
      ) : chartData.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-white/10">
          <DollarSign className="w-10 h-10 text-stone-700 mx-auto mb-3" />
          <p className="text-sm text-stone-500">No price data yet.</p>
          {isAdmin && (
            <p className="text-xs text-stone-600 mt-1">Click &quot;Add Market Price&quot; to start tracking.</p>
          )}
        </div>
      ) : (
        <div className="h-80 w-full" style={{ minHeight: 320, minWidth: 280 }} data-testid="price-chart">
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#292524" />
              <XAxis dataKey="date" stroke="#78716c" fontSize={11} />
              <YAxis stroke="#78716c" fontSize={11} tickFormatter={(v) => `$${v}`} />
              <Tooltip content={<TooltipContent />} />
              {latestMarket != null && (
                <ReferenceLine y={latestMarket} stroke="#d4af3744" strokeDasharray="3 3" />
              )}
              <Line
                type="monotone"
                dataKey="market"
                stroke="#d4af37"
                strokeWidth={2}
                dot={{ fill: '#d4af37', r: 4 }}
                connectNulls
                name="Market"
              />
              <Line
                type="monotone"
                dataKey="purchase"
                stroke="#9f1239"
                strokeWidth={2}
                dot={{ fill: '#9f1239', r: 4 }}
                connectNulls
                name="My Purchase"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Admin: list & delete */}
      {isAdmin && marketPrices.length > 0 && (
        <div className="mt-6 border-t border-white/5 pt-4">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 mb-3">Market price points</p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {marketPrices.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between text-sm text-stone-300 py-2 px-3 bg-stone-950/30 border border-white/5"
                data-testid={`price-row-${p.id}`}
              >
                <span className="text-stone-500 text-xs">{(p.recorded_at || '').slice(0, 10)}</span>
                <span className="font-mono text-[#d4af37]">{fmtMoney(p.price, p.currency)}</span>
                <span className="text-stone-500 text-xs flex-1 ml-4">{p.source || '—'}</span>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="text-stone-600 hover:text-red-400 transition-colors"
                  data-testid={`delete-price-${p.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WinePriceHistory;
